
    const data = [
  {
    "title": "Principles",
    "id": "principles",
    "elements": [
      {
        "type": "paragraph",
        "text": "This framework is intended to help your team coordinate the rollout of new digital tools/models/systems alongside changes to the scientific process that will enable the tools."
      },
      {
        "type": "paragraph",
        "text": "The approach is based on the following principles:"
      },
      {
        "type": "heading",
        "size": 2,
        "title": "1. The product is scientific process, not digital tools"
      },
      {
        "type": "paragraph",
        "text": "Whether drug discovery teams develop their own internal tools/models/systems or adopt externally developed software, they should conceive of their objective as updating the drug discovery process via these tools, rather than just as designing a digital product that they can hand off to the scientists."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "2. Scientists are co-developers, not just users"
      },
      {
        "type": "paragraph",
        "text": "Because the product is the scientific process, the bench scientists who manage that process are more than just users whose requirements need to be satisfied. Digital developers should expand their conception of the development team to include bench scientists who will develop the new processes in parallel with digital development."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "3. You can't design beyond what you can see"
      },
      {
        "type": "paragraph",
        "text": "Bench scientists can't build new processes without seeing what the digital tools that will support the process are capable of. Digital teams can't build and demonstrate those tools without seeing the process they will need to support. The only way to solve this chicken and egg problem is to evolve the processes and the digital tools in parallel, incrementally revealing the full picture."
      },
      {
        "type": "paragraph",
        "text": "Most digital teams are familiar with the agile development and the idea of building software incrementally. The principles above can be thought of as extending the agile process to include updating the scientific process and the bench scientists who will implement those changes. The co-development framework is built around this idea."
      }
    ]
  },
  {
    "title": "Flexibility vs Consistency",
    "id": "flexibility",
    "elements": [
      {
        "type": "paragraph",
        "text": "The fundamental tension when introducing digital technology into drug discovery is the tradeoff between flexibility and consistency. Teams need flexibility to navigate the messy realities of biology, a science where there are more exceptions than rules. But they also need consistency to make results comparable and reproducible across experiments."
      },
      {
        "type": "paragraph",
        "text": "The ideal balance between the two will depend on context and will often change over time. To make sure that everyone is on the same page about finding that balance, teams need a common language for discussing this tradeoff, and some ground rules for making decisions. That's the goal of this section."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Required vs Optional"
      },
      {
        "type": "paragraph",
        "text": "Flexibility and consistency are diametrically opposing forces. The only way to make a process consistent is to give yourself less flexibility in how it's achieved. However, some sources of flexibility or consistency are more important than others."
      },
      {
        "type": "paragraph",
        "text": "Some flexibility is required by the science. For example, if an assay is run with different cell lines from experiment to experiment depending on scientific factors, the cell line is a type of required flexibility. On the other hand, if the cell line column in the metadata is sometime capitalized and sometimes all lower-case, that's optional flexibility."
      },
      {
        "type": "paragraph",
        "text": "If the cell line is always the same in practice, but the bench team thinks they might need to change it in the future, it might be required or optional. That's a discussion. And if they use different cell lines initially before settling on one that they use consistently, it can switch from required to optional."
      },
      {
        "type": "paragraph",
        "text": "Similarly, there's a difference between consistency that's required, for example to prevent ambiguity in how data is interpreted, and optional consistency that just makes life easier for downstream users. For example, maybe a field name is sometimes capitalized and sometimes lower case, or sometimes it's the second column and sometimes it's the fifth. That's a pain for the analyst but it unambiguous. (And it's less of a pain with the help of an LLM/agent.)"
      },
      {
        "type": "paragraph",
        "text": "On the other hand, if the field is sometimes called \"cell_line\" and sometimes it's called \"line\", a downstream user might think that \"line\" refers to something else. And if the field is completely missing, that's definitely a problem. So some kind of standardization of this field is required consistency, but beyond that it may be optional."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Rules of Thumb"
      },
      {
        "type": "paragraph",
        "text": "The decision of what kinds of consistency to enforce and what kinds of flexibility to allow will always be a negotiation between the bench and digital teams. But as a rule of thumb, you should start with the following rules, enforced in order:"
      },
      {
        "type": "bullets",
        "bullets": [
          "1. Allow any required flexibility. The science comes first."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "2. Enforce all required consistency that doesn't conflict with required flexibility."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "3. Evolve towards removing optional flexibility and enforcing optional consistency."
        ]
      },
      {
        "type": "paragraph",
        "text": "A large part of iterative co-development is introducing tools and evolving processes to enforce required consistency as quickly as possible, then enforcing optional consistency in ways that will prepare you for when areas of required flexibility become optional."
      }
    ]
  },
  {
    "title": "Mechanical Turks",
    "id": "prototypes",
    "elements": [
      {
        "type": "paragraph",
        "text": "In the early stages of an Agile development project, it's common to build temporary versions of some of the components that will be thrown away and replaced later. The goal is to get to an MVP - a minimal, end-to-end working version of the product that you can upgrade piece by piece while keeping it working the whole time. You can't design beyond what you can see."
      },
      {
        "type": "paragraph",
        "text": "If the product is just the digital tool/model/system then each of these throw-away components is typically a piece of code within the repo where the final system will live. It might be a minimal implementation, or just a placeholder that returns empty results, but it's the same \"shape\" as the final version."
      },
      {
        "type": "paragraph",
        "text": "When the product is the scientific process and the scope of the end-to-end MVP is larger, however, the scope of the throw-away placeholder components is also much larger. In many cases, it will make sense to implement these components in a form that's very different from what their final stage will look like, with a human behind the scenes, i.e. a [Mechanical Turk](https://en.wikipedia.org/wiki/Mechanical_Turk)."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "An Example"
      },
      {
        "type": "paragraph",
        "text": "Let's say your digital team is developing a model that designs new compounds based on past screening results. In the final system, screening results will be automatically uploaded, processed and fed into the model, then the model's predictions/designs will be displayed in a web app where they can be reviewed, before being sent off for synthesis."
      },
      {
        "type": "paragraph",
        "text": "If the product is this digital system, then the MVP might start with a placeholder model that selects random designs so you can build the user interface. Then you might build out the workflows to process screening results, before going back and developing the real model."
      },
      {
        "type": "paragraph",
        "text": "In this framing, being able to see what the data needs to look like in the user interface, and what the screening data will look like helps the development team understand what the inputs and outputs of the model need to look like. That's because the input and output requirements for the model are defined by the expected format of the screening data and the design of the user interface."
      },
      {
        "type": "paragraph",
        "text": "However, if the product is the scientific process then the requirements for the model are defined by the type of screening experiments that the bench team actually runs and the types of questions they ask about the predictions from the model. So the placeholder for the components that get data to and from the model are between the scientists and the model, not between technical components."
      },
      {
        "type": "paragraph",
        "text": "In particular, these processes need to be flexible enough to adjust to any required flexibility that turns out to be in the scientific process, including flexibility that wasn't identified in the initial design."
      },
      {
        "type": "paragraph",
        "text": "The best placeholder component for this kind of flexibility probably isn't code. It's a person and an email address: The bench team sends the screening results as a CSV file attached to an email that explains the format. The data scientist generates a CSV, then screen-shares the results to the bench team and answers their questions verbally. In between, they'll manually reformat the input data and run the model."
      },
      {
        "type": "paragraph",
        "text": "These placeholder components won't make it easier to write the code for the final end-to-end digital system, but they will surface requirements that weren't identified earlier, including sources of required flexibility. And that will ensure the final system fits into the scientific process."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Speed and Flexibility"
      },
      {
        "type": "paragraph",
        "text": "The goal of this approach is to provide the flexibility that the scientific process requires while getting a realistic understanding of those requirements as quickly as possible. Today's agent-based coding systems would probably allow the digital team to build an end-to-end digital prototype in a week or two, but that still wouldn't provide the same level of flexibility as a prototype shared as a spreadsheet."
      },
      {
        "type": "paragraph",
        "text": "Even a few hours on an iteration cycle adds up when you're coordinating with a busy team. And an extra week or two in developing an MVP could mean significant changes to the scientific process. A mechanical turk is often the safest way to start, until you know the scientific process is stable."
      }
    ]
  },
  {
    "title": "Catching the Bus",
    "id": "bus",
    "elements": [
      {
        "type": "paragraph",
        "text": "One reason it's so important to get to an MVP so quickly, and continue to iterate quickly, is that early discovery timelines tend to be long an unwieldy. A single experiment can take weeks or longer from planning to completion, and once things are set in motion, it can be very difficult to make changes."
      },
      {
        "type": "paragraph",
        "text": "When a digital team asks the bench team to try out a new predictive model or change a workflow to collect data more consistently, they often find they're just too late for the next convenient experiment."
      },
      {
        "type": "paragraph",
        "text": "To keep timelines reasonable, they usually need to start planning for this long before the model or tool is ready. The analogy I've found most helpful catching a bus."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The experiment is the bus"
      },
      {
        "type": "paragraph",
        "text": "If you want to take a bus, you don't wait at home until you see it at the bus stop. By the time you get there, it will have left. The same thing happens if you wait until an experiment is about to start before you ask the bench team to do something differently."
      },
      {
        "type": "paragraph",
        "text": "In the same way that you need to start walking towards the bus stop before the bus gets there, you need to start planning and advocating for any changes to the experimental process long before any experiment that is going to be affected, first to convince everyone involved to try something new and second to work out the details and logistics."
      },
      {
        "type": "paragraph",
        "text": "Often, this comes down to asking different people \"When is the next experiment we can try this on?\" over and over until you find someone who gives you a reasonable answer. At the risk of mixing metaphors, this kind of cat herding is a large, often overlooked, part of integrating digital models into drug discovery."
      },
      {
        "type": "paragraph",
        "text": "Asking this question over and over again is a relatively simple task, but there are two keys to getting it right:"
      },
      {
        "type": "bullets",
        "bullets": [
          "1. You need to start early, probably before the digital tool is even ready, since the next possible experiment may be weeks or months away."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "2. You need to be able to quickly adapt to any unexpected requirements of the experiment."
        ]
      },
      {
        "type": "paragraph",
        "text": "For that second reason in particular, it's important to be able to flexibly adapt your end-to-end MVP as quickly as possible. If you miss the bus by a day, it could set you back months."
      }
    ]
  }
]

    export default data;
    